import { ActionIcon, Menu, Text, TextInput, Tooltip } from '@mantine/core';
import { Icon } from '@uag/react-core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CopilotConversation } from 'app/modules/wallforming/components/copilot/types/copilotTypes';

interface CopilotConversationMenuProps {
    conversations: CopilotConversation[];
    activeConversationId: string;
    isSending: boolean;
    isApplying: boolean;
    onCreate: () => void;
    onSelect: (id: string) => void;
    onDelete: (id: string) => void;
    onRename: (id: string, title: string) => void;
}

export const CopilotConversationMenu = ({
    conversations,
    activeConversationId,
    isSending,
    isApplying,
    onCreate,
    onSelect,
    onDelete,
    onRename
}: CopilotConversationMenuProps) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
    const [nameDraft, setNameDraft] = useState('');
    const isBusy = isSending || isApplying;

    const beginRename = (conversation: CopilotConversation) => {
        setEditingConversationId(conversation.id);
        setNameDraft(conversation.title);
    };

    const saveRename = () => {
        const title = nameDraft.trim();
        if (!title || !editingConversationId) {
            return;
        }

        onRename(editingConversationId, title);
        setEditingConversationId(null);
    };

    return (
        <div className="flex shrink-0 items-center justify-end gap-1 px-3 py-2">
            <Tooltip label={t('copilotNewChat')}>
                <ActionIcon
                    aria-label={t('copilotNewChat')}
                    className="bg-background-gray-ultra-light hover:bg-background-gray!"
                    disabled={isBusy}
                    variant="subtle"
                    onClick={() => {
                        onCreate();
                        setIsOpen(false);
                        setEditingConversationId(null);
                    }}
                >
                    <Icon aria-hidden="true" icon="add" size="sm" />
                </ActionIcon>
            </Tooltip>
            <Menu closeOnItemClick={false} opened={isOpen} position="bottom-end" shadow="md" onChange={setIsOpen}>
                <Menu.Target>
                    <Tooltip label={t('copilotChats')}>
                        <ActionIcon
                            aria-expanded={isOpen}
                            aria-label={t('copilotChats')}
                            className="bg-background-gray-ultra-light hover:bg-background-gray!"
                            variant={isOpen ? 'light' : 'subtle'}
                        >
                            <Icon aria-hidden="true" icon="forum" size="sm" />
                        </ActionIcon>
                    </Tooltip>
                </Menu.Target>
                <Menu.Dropdown className="max-h-80 min-w-80 overflow-y-auto">
                    <Menu.Label>{t('copilotChats')}</Menu.Label>
                    <Menu.Divider />
                    {conversations.map((conversation) => (
                        <div
                            key={conversation.id}
                            className={`flex items-center gap-1 px-2 py-1 ${
                                conversation.id === activeConversationId ? 'rounded-sm bg-background-action-hover' : ''
                            }`}
                        >
                            {editingConversationId === conversation.id ? (
                                <>
                                    <TextInput
                                        aria-label={t('copilotRenameChat')}
                                        className="min-w-0 flex-1"
                                        size="xs"
                                        value={nameDraft}
                                        onChange={(event) => setNameDraft(event.currentTarget.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                                event.preventDefault();
                                                saveRename();
                                            } else if (event.key === 'Escape') {
                                                setEditingConversationId(null);
                                            }
                                        }}
                                    />
                                    <Tooltip label={t('copilotSaveChatName')}>
                                        <ActionIcon
                                            aria-label={t('copilotSaveChatName')}
                                            disabled={!nameDraft.trim()}
                                            size="sm"
                                            variant="subtle"
                                            onClick={saveRename}
                                        >
                                            <Icon aria-hidden="true" icon="check" size="xs" />
                                        </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label={t('cancel')}>
                                        <ActionIcon
                                            aria-label={t('cancel')}
                                            size="sm"
                                            variant="subtle"
                                            onClick={() => setEditingConversationId(null)}
                                        >
                                            <Icon aria-hidden="true" icon="close" size="xs" />
                                        </ActionIcon>
                                    </Tooltip>
                                </>
                            ) : (
                                <>
                                    <Text className="min-w-0 flex-1 truncate text-sm" title={conversation.title}>
                                        {conversation.title}
                                    </Text>
                                    <Tooltip label={t('copilotOpenChat')}>
                                        <ActionIcon
                                            aria-label={t('copilotOpenChat')}
                                            disabled={isBusy}
                                            size="sm"
                                            variant="subtle"
                                            onClick={() => {
                                                onSelect(conversation.id);
                                                setIsOpen(false);
                                                setEditingConversationId(null);
                                            }}
                                        >
                                            <Icon aria-hidden="true" icon="open_in_new" size="xs" />
                                        </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label={t('copilotRenameChat')}>
                                        <ActionIcon
                                            aria-label={t('copilotRenameChat')}
                                            disabled={isBusy}
                                            size="sm"
                                            variant="subtle"
                                            onClick={() => beginRename(conversation)}
                                        >
                                            <Icon aria-hidden="true" icon="edit" size="xs" />
                                        </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label={t('copilotDeleteChat')}>
                                        <ActionIcon
                                            aria-label={t('copilotDeleteChat')}
                                            disabled={isBusy}
                                            size="sm"
                                            variant="subtle"
                                            onClick={() => onDelete(conversation.id)}
                                        >
                                            <Icon aria-hidden="true" icon="delete" size="xs" />
                                        </ActionIcon>
                                    </Tooltip>
                                </>
                            )}
                        </div>
                    ))}
                </Menu.Dropdown>
            </Menu>
        </div>
    );
};
